import * as React from 'react';
import { memo, ReactNode, useCallback, useEffect, useState } from 'react';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { PATHS } from 'router/config';
import { useLogOut, usePalette, useUser } from 'core/hooks';
import { UserRole } from 'types';

import { MenuItem } from 'molecules';
import { MenuComponent } from './Menu';

/** A single navigable page inside a top-level menu group. */
interface IMenuLeaf {
  text: string;
  to: string;
  /** Path prefix used for active-state detection; defaults to `to`. */
  match?: string;
  roles: UserRole[];
}

/** A top-level menu entry — renders as a dropdown when 2+ leaves are visible. */
interface IMenuGroup {
  text: string;
  children: IMenuLeaf[];
}

const STUDENT_ROLES = [
  UserRole.ADMIN,
  UserRole.ACCOUNTANT,
  UserRole.SALESMANAGER,
  UserRole.FINANCIER,
];

const EDUCATION_ROLES = [
  UserRole.ADMIN,
  UserRole.ACCOUNTANT,
  UserRole.SALESMANAGER,
  UserRole.HRMANAGER,
];

const getMenuStructure = (t: TFunction): IMenuGroup[] => [
  {
    text: `${t('menu.students')}`,
    children: [
      {
        text: `${t('menu.dashboard')}`,
        to: PATHS.dashboard,
        roles: STUDENT_ROLES,
      },
      {
        text: `${t('menu.communicator')}`,
        to: PATHS.communicator,
        roles: STUDENT_ROLES,
      },
      {
        text: `${t('menu.students')}`,
        to: PATHS.students,
        roles: STUDENT_ROLES,
      },
      { text: `${t('menu.debtors')}`, to: PATHS.debtors, roles: STUDENT_ROLES },
      {
        text: `${t('menu.performance')}`,
        to: PATHS.performance,
        roles: [UserRole.ADMIN, UserRole.ACCOUNTANT, UserRole.HRMANAGER],
      },
      {
        text: `${t('menu.pandadoc')}`,
        to: PATHS.debtorsPandadoc,
        match: PATHS.debtorsPandadoc,
        roles: STUDENT_ROLES,
      },
      {
        text: `${t('menu.pandadocProvisioning')}`,
        to: PATHS.debtorsPandadocProvisioning,
        roles: STUDENT_ROLES,
      },
    ],
  },
  {
    text: `${t('menu.education')}`,
    children: [
      {
        text: `${t('menu.courses')}`,
        to: PATHS.settingsCourses,
        roles: EDUCATION_ROLES,
      },
      {
        text: `${t('menu.groups')}`,
        to: PATHS.settingsGroups,
        roles: EDUCATION_ROLES,
      },
      {
        text: `${t('menu.journals')}`,
        to: PATHS.settingsJournals,
        roles: EDUCATION_ROLES,
      },
      {
        text: `${t('menu.teachers')}`,
        to: PATHS.teachers,
        roles: EDUCATION_ROLES,
      },
    ],
  },
  {
    text: `${t('menu.finances')}`,
    children: [
      {
        text: `${t('menu.financesDashboard')}`,
        to: PATHS.financesDashboard,
        match: PATHS.financesDashboard,
        roles: [
          UserRole.ADMIN,
          UserRole.FINANCIER,
          UserRole.ACCOUNTANT,
          UserRole.SALESMANAGER,
        ],
      },
      {
        text: `${t('menu.financesInvoices')}`,
        to: PATHS.financesInvoices,
        match: PATHS.financesInvoices,
        roles: [
          UserRole.ADMIN,
          UserRole.FINANCIER,
          UserRole.ACCOUNTANT,
          UserRole.SALESMANAGER,
        ],
      },
      {
        text: `${t('menu.financesExpenses')}`,
        to: PATHS.financesExpenses,
        match: PATHS.financesExpenses,
        roles: [UserRole.ADMIN, UserRole.FINANCIER, UserRole.ACCOUNTANT],
      },
      {
        text: `${t('menu.financesPlan')}`,
        to: PATHS.finances,
        match: PATHS.finances,
        roles: [UserRole.ADMIN, UserRole.FINANCIER],
      },
      {
        text: `${t('menu.financesPaymentCalendar')}`,
        to: PATHS.financesPaymentCalendar,
        match: PATHS.financesPaymentCalendar,
        roles: [UserRole.ADMIN, UserRole.FINANCIER],
      },
    ],
  },
  {
    text: `${t('menu.system')}`,
    children: [
      { text: `${t('menu.users')}`, to: PATHS.users, roles: [UserRole.ADMIN] },
      {
        text: `${t('menu.zoomRecordings')}`,
        to: PATHS.zoomDashboard,
        match: PATHS.zoom,
        roles: [UserRole.ADMIN, UserRole.ACCOUNTANT, UserRole.METHODIST],
      },
    ],
  },
];

/** True when the current path equals the base or is nested under it. */
const isPathActive = (pathname: string, base: string): boolean =>
  pathname === base || pathname.startsWith(`${base}/`);

export const Menu = memo(() => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [menuItems, setMenuItems] = useState<ReactNode[] | null>([null]);
  const [{ user, loading }] = useUser();
  const { openPalette } = usePalette();

  const logOut = useLogOut();

  const getMenuByRoles = useCallback(
    (userRoles: UserRole[]): ReactNode[] =>
      getMenuStructure(t).reduce((result: ReactNode[], group) => {
        const visible = group.children.filter((leaf) =>
          leaf.roles.some((role) => userRoles.includes(role)),
        );
        if (!visible.length) {
          return result;
        }

        const isActive = visible.some((leaf) =>
          isPathActive(pathname, leaf.match ?? leaf.to),
        );
        // A lone visible leaf renders as a plain item with its own label.
        const single = visible.length === 1;

        return [
          ...result,
          <MenuItem
            key={group.text}
            to={visible[0].to}
            isActive={isActive}
            isMulti={
              single
                ? undefined
                : visible.map((leaf) => ({
                    children: leaf.text,
                    to: leaf.to,
                  }))
            }
          >
            {single ? visible[0].text : group.text}
          </MenuItem>,
        ];
      }, []),
    [pathname, t],
  );

  useEffect(() => {
    if (!loading && user?.roles && user.roles.length > 0) {
      setMenuItems(getMenuByRoles(user?.roles as UserRole[]));
    }
  }, [user, loading, getMenuByRoles]);

  return (
    <MenuComponent
      onExit={logOut}
      onOpenPalette={openPalette}
      menuItems={menuItems || []}
      userFirstName={user?.firstName}
      userLastName={user?.lastName}
      userEmail={user?.email}
    />
  );
});
